import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  AccountCircle as AccountCircleIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import ValidationAssignUserNeedDialog from '../dialogs/ValidationAssignUserNeedDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useFindProductUserNeedsByValidationId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductUserNeed,
  BaseProductValidationCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';

const ValidationUserNeedReferenceCard: React.FC<BaseProductValidationCardProps> = (
  props: BaseProductValidationCardProps
) => {
  const {
    productId,
    productValidation,
    productValidationVersion,
    onUpdate,
    onError
  } = props;
  const history = useHistory();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
  const [order, setOrder] = useState<string | undefined>();
  const [orderDirection, setOrderDirection] = useState<
    'ASC' | 'DESC' | undefined
  >();
  const {
    productUserNeedList,
    isLoadingProductUserNeeds,
    loadingProductUserNeedsError,
    findProductUserNeeds
  } = useFindProductUserNeedsByValidationId(productId, productValidation.id);
  const { isFieldDisabled } = useTaskEntityFields(productValidation?.task);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.UserNeed
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const findProductUserNeedsWithParams = useCallback(() => {
    const params: ApiParams = {};
    if (order) params.order = order;
    if (orderDirection) params.direction = orderDirection;
    if (productValidationVersion)
      params.version = productValidationVersion.version;
    findProductUserNeeds(params);
  }, [findProductUserNeeds, order, orderDirection, productValidationVersion]);

  useEffect(() => {
    findProductUserNeedsWithParams();
  }, [findProductUserNeedsWithParams]);

  useEffect(() => {
    if (loadingProductUserNeedsError) {
      onError?.(loadingProductUserNeedsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductUserNeedsError]);
  const disabled =
    Boolean(productValidationVersion) || isFieldDisabled('assignUserNeed');
  return (
    <Card>
      <CardTitle>
        <AccountCircleIcon />
        {getProductEntityName('userNeed')} Reference
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.id'}
                  direction={getSortDirection('productUserNeed.id')}
                  onClick={() => handleSort('productUserNeed.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.title'}
                  direction={getSortDirection('productUserNeed.title')}
                  onClick={() => handleSort('productUserNeed.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductUserNeeds && (
              <TableRow>
                <TableCell colSpan={2} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productUserNeedList?.map((userNeed: ProductUserNeed) => (
              <TableRow
                hover
                key={userNeed.id}
                onClick={() => {
                  history.push(
                    `/portal/products/${productId}/user-needs/${userNeed.id}`
                  );
                }}
              >
                <TableCell>{prefix + userNeed.id}</TableCell>
                <WrappedTableCell>{userNeed.title}</WrappedTableCell>
                <TableCell>
                  <TaskStatusLabel task={userNeed.task} />
                </TableCell>
                <TableCell
                  style={{
                    textAlign: 'center'
                  }}
                >
                  {!disabled && (
                    <CloseIcon
                      onClick={event => {
                        event.stopPropagation();
                        setItemToUnlink(userNeed.id);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => setOpenDialog(true)}
                  disabled={disabled}
                >
                  Assign {getProductEntityName('userNeed')}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog && (
        <ValidationAssignUserNeedDialog
          open={openDialog}
          productId={productId}
          validationId={productValidation.id}
          selectedItems={productUserNeedList}
          onError={onError}
          onCancel={() => setOpenDialog(false)}
          onAssigned={() => {
            findProductUserNeedsWithParams();
            onUpdate?.();
            setOpenDialog(false);
          }}
        />
      )}
      <UnlinkConfirmDialog
        open={!!itemToUnlink}
        productId={productId}
        entityId={productValidation.id}
        from={ProductBoardColumnEntity.Validation}
        to={ProductBoardColumnEntity.UserNeed}
        unlinkItemId={itemToUnlink}
        selectedItems={productUserNeedList}
        onError={onError}
        onCancel={() => setItemToUnlink(undefined)}
        onChange={() => {
          findProductUserNeedsWithParams();
          onUpdate?.();
          setItemToUnlink(undefined);
        }}
      />
    </Card>
  );
};

export default ValidationUserNeedReferenceCard;
